import React, { useMemo, useState } from "react";
import { Card, CardContent } from "@/components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  ResponsiveContainer,
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  Legend,
  CartesianGrid,
} from "recharts";

import {
  TrendingUp,
  TrendingDown,
  Minus,
  Receipt,
  DollarSign,
  Users,
  Accessibility,
  Map as MapIcon,
  Car,
  User,
} from "lucide-react";
import { RideWiseRow } from "./types";
import { fmtMoney, fmtNum, safeArr } from "./helpers";

interface Props {
  rw: RideWiseRow[];
  prevRw: RideWiseRow[];
  allRw?: RideWiseRow[];
  selectedYears?: string[];
  selectedMonths?: string[];
  loading: boolean;
}

interface Totals {
  trans: number;
  fareTotal: number;
  subsidy: number;
  farePaid: number;
  pax: number;
  hoist: number;
  km: number;
  vehicles: Set<string>;
  drivers: Set<string>;
}

function totalsOf(rows: RideWiseRow[]): Totals {
  const t: Totals = {
    trans: 0,
    fareTotal: 0,
    subsidy: 0,
    farePaid: 0,
    pax: 0,
    hoist: 0,
    km: 0,
    vehicles: new Set(),
    drivers: new Set(),
  };
  for (const r of safeArr(rows)) {
    t.trans++;
    t.fareTotal += Number(r.fare_total || 0);
    t.subsidy += Number(r.concession_amount || 0);
    t.farePaid += Number(r.fare_paid || 0);
    t.pax += Number(r.number_of_passengers || 0);
    t.km += Number(r.kilometres || 0);
    if (r.hoist_deployed) t.hoist++;
    if (r.vehicle_identifier) t.vehicles.add(String(r.vehicle_identifier));
    if (r.driver_number) t.drivers.add(String(r.driver_number));
  }
  return t;
}

function trend(curr: number, prev: number): { pct: number; dir: "up" | "down" | "flat" } {
  if (!prev) return { pct: 0, dir: curr > 0 ? "up" : "flat" };
  const pct = ((curr - prev) / prev) * 100;
  if (Math.abs(pct) < 0.5) return { pct, dir: "flat" };
  return { pct, dir: pct > 0 ? "up" : "down" };
}

const TrendBadge: React.FC<{ curr: number; prev: number }> = ({ curr, prev }) => {
  const t = trend(curr, prev);
  const Icon =
    t.dir === "up" ? TrendingUp : t.dir === "down" ? TrendingDown : Minus;
  const cls =
    t.dir === "up"
      ? "text-green-500"
      : t.dir === "down"
      ? "text-red-500"
      : "text-muted-foreground";
  return (
    <span className={`inline-flex items-center gap-1 text-xs ${cls}`}>
      <Icon className="w-3 h-3" />
      {t.dir === "flat" ? "—" : `${t.pct.toFixed(1)}%`}
    </span>
  );
};

const StatCard: React.FC<{
  title: string;
  value: string;
  icon: React.ReactNode;
  curr: number;
  prev: number;
}> = ({ title, value, icon, curr, prev }) => (
  <Card className="bg-card border border-border">
    <CardContent className="pt-3 pb-3">
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <div className="text-[11px] text-muted-foreground truncate">
            {title}
          </div>
          <div className="text-lg font-bold mt-0.5 truncate">{value}</div>
          <div className="mt-0.5">
            <TrendBadge curr={curr} prev={prev} />
            <span className="text-[10px] text-muted-foreground ml-1">
              vs prev
            </span>
          </div>
        </div>
        <div className="text-yellow-500 shrink-0">{icon}</div>
      </div>
    </CardContent>
  </Card>
);

type ChartMetric =
  | "fareTotal"
  | "subsidy"
  | "farePaid"
  | "trans"
  | "pax"
  | "hoist"
  | "km";

const METRIC_OPTIONS: { value: ChartMetric; label: string; money?: boolean }[] = [
  { value: "fareTotal", label: "Fare Total", money: true },
  { value: "subsidy", label: "Subsidy", money: true },
  { value: "farePaid", label: "Fare Paid", money: true },
  { value: "trans", label: "Transactions" },
  { value: "pax", label: "Passengers" },
  { value: "hoist", label: "Hoist Trips" },
  { value: "km", label: "Kilometres" },
];

const MONTH_LABELS = [
  "Jan", "Feb", "Mar", "Apr", "May", "Jun",
  "Jul", "Aug", "Sep", "Oct", "Nov", "Dec",
];

// Distinct, accessible colours for year lines
const YEAR_COLOURS = [
  "#eab308", // amber/gold (current accent)
  "#3b82f6", // blue
  "#22c55e", // green
  "#a855f7", // purple
  "#ef4444", // red
  "#06b6d4", // cyan
  "#f97316", // orange
  "#ec4899", // pink
  "#14b8a6", // teal
  "#84cc16", // lime
];

const OverviewTab: React.FC<Props> = ({
  rw,
  prevRw,
  allRw,
  selectedYears,
  selectedMonths,
  loading,
}) => {
  const t = useMemo(() => totalsOf(rw), [rw]);
  const p = useMemo(() => totalsOf(prevRw), [prevRw]);

  const avgVal = t.trans ? t.fareTotal / t.trans : 0;
  const avgSub = t.trans ? t.subsidy / t.trans : 0;
  const pAvgVal = p.trans ? p.fareTotal / p.trans : 0;
  const pAvgSub = p.trans ? p.subsidy / p.trans : 0;

  const [metric, setMetric] = useState<ChartMetric>("fareTotal");
  const metricCfg =
    METRIC_OPTIONS.find((m) => m.value === metric) || METRIC_OPTIONS[0];

  // Build per-year, per-month series from allRw (all years) so we can draw
  // one colour-coded line per year. Honour selectedMonths so the user can
  // narrow the x-axis to just the months they care about.
  const { chartData, yearsToPlot } = useMemo(() => {
    const source = safeArr(allRw && allRw.length ? allRw : rw);

    const monthsAllowed =
      !selectedMonths ||
      selectedMonths.includes("all") ||
      selectedMonths.length === 0
        ? null
        : new Set(selectedMonths);

    // year -> 12-element array of metric totals
    const yearMap = new Map<string, number[]>();

    for (const r of source) {
      const date =
        (r as any).trip_date ||
        (r as any).Trip_Date ||
        (r as any).pu_date ||
        (r as any).created_at ||
        "";
      const iso = String(date).slice(0, 10);
      if (iso.length < 7) continue;
      const year = iso.slice(0, 4);
      const monthStr = iso.slice(5, 7);
      if (monthsAllowed && !monthsAllowed.has(monthStr)) continue;
      const monthIdx = Number(monthStr) - 1;
      if (monthIdx < 0 || monthIdx > 11) continue;

      let arr = yearMap.get(year);
      if (!arr) {
        arr = new Array(12).fill(0);
        yearMap.set(year, arr);
      }

      let value = 0;
      switch (metric) {
        case "fareTotal":
          value = Number(r.fare_total || 0);
          break;
        case "subsidy":
          value = Number(r.concession_amount || 0);
          break;
        case "farePaid":
          value = Number(r.fare_paid || 0);
          break;
        case "trans":
          value = 1;
          break;
        case "pax":
          value = Number(r.number_of_passengers || 0);
          break;
        case "hoist":
          value = r.hoist_deployed ? 1 : 0;
          break;
        case "km":
          value = Number(r.kilometres || 0);
          break;
      }
      arr[monthIdx] += value;
    }

    // Decide which years to actually plot.
    let years: string[];
    if (
      selectedYears &&
      !selectedYears.includes("all") &&
      selectedYears.length > 0
    ) {
      const requested = new Set(selectedYears);
      // Always include the year before the earliest selected year as a comparison line
      const minYear = Math.min(...selectedYears.map((y) => Number(y)));
      if (Number.isFinite(minYear)) {
        requested.add(String(minYear - 1));
      }
      years = Array.from(requested)
        .filter((y) => yearMap.has(y))
        .sort();
    } else {
      years = Array.from(yearMap.keys()).sort();
    }

    // Build one row per month with year keys
    const data = MONTH_LABELS.map((label, i) => {
      const row: Record<string, number | string> = { month: label };
      for (const y of years) {
        const arr = yearMap.get(y);
        row[y] = arr ? arr[i] : 0;
      }
      return row;
    });

    return { chartData: data, yearsToPlot: years };
  }, [allRw, rw, metric, selectedYears, selectedMonths]);

  const formatTick = (v: number) =>
    metricCfg.money
      ? `$${(v / 1000 >= 1 ? (v / 1000).toFixed(1) + "k" : v.toFixed(0))}`
      : fmtNum(v);

  const formatValue = (v: number) =>
    metricCfg.money ? fmtMoney(v) : fmtNum(v);

  if (loading) {
    return (
      <Card className="bg-card border border-border">
        <CardContent className="py-12 text-center text-muted-foreground">
          Loading overview…
        </CardContent>
      </Card>
    );
  }

  if (!safeArr(rw).length) {
    return (
      <Card className="bg-card border border-border">
        <CardContent className="py-12 text-center text-muted-foreground">
          No RideWise data in the selected period.
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
      {/* Tiles - left half */}
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
        <StatCard
          title="Transactions"
          value={fmtNum(t.trans)}
          icon={<Receipt className="w-4 h-4" />}
          curr={t.trans}
          prev={p.trans}
        />
        <StatCard
          title="Fare Total"
          value={fmtMoney(t.fareTotal)}
          icon={<DollarSign className="w-4 h-4" />}
          curr={t.fareTotal}
          prev={p.fareTotal}
        />
        <StatCard
          title="Subsidy"
          value={fmtMoney(t.subsidy)}
          icon={<DollarSign className="w-4 h-4" />}
          curr={t.subsidy}
          prev={p.subsidy}
        />
        <StatCard
          title="Fare Paid"
          value={fmtMoney(t.farePaid)}
          icon={<DollarSign className="w-4 h-4" />}
          curr={t.farePaid}
          prev={p.farePaid}
        />
        <StatCard
          title="Passengers"
          value={fmtNum(t.pax)}
          icon={<Users className="w-4 h-4" />}
          curr={t.pax}
          prev={p.pax}
        />
        <StatCard
          title="Hoist Trips"
          value={fmtNum(t.hoist)}
          icon={<Accessibility className="w-4 h-4" />}
          curr={t.hoist}
          prev={p.hoist}
        />
        <StatCard
          title="Kilometres"
          value={fmtNum(t.km, 1)}
          icon={<MapIcon className="w-4 h-4" />}
          curr={t.km}
          prev={p.km}
        />
        <StatCard
          title="Avg Transaction"
          value={fmtMoney(avgVal)}
          icon={<DollarSign className="w-4 h-4" />}
          curr={avgVal}
          prev={pAvgVal}
        />
        <StatCard
          title="Avg Subsidy"
          value={fmtMoney(avgSub)}
          icon={<DollarSign className="w-4 h-4" />}
          curr={avgSub}
          prev={pAvgSub}
        />
        <StatCard
          title="Vehicles"
          value={fmtNum(t.vehicles.size)}
          icon={<Car className="w-4 h-4" />}
          curr={t.vehicles.size}
          prev={p.vehicles.size}
        />
        <StatCard
          title="Drivers"
          value={fmtNum(t.drivers.size)}
          icon={<User className="w-4 h-4" />}
          curr={t.drivers.size}
          prev={p.drivers.size}
        />
      </div>

      {/* Chart - right half */}
      <Card className="bg-card border border-border">
        <CardContent className="pt-4">
          <div className="flex items-center justify-between mb-3 gap-2">
            <div>
              <div className="text-sm font-semibold">Monthly trend by year</div>
              <div className="text-[11px] text-muted-foreground">
                One line per year · totals aggregated by month
              </div>
            </div>
            <Select
              value={metric}
              onValueChange={(v) => setMetric(v as ChartMetric)}
            >
              <SelectTrigger className="dark-input w-44 h-8 text-xs">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {METRIC_OPTIONS.map((opt) => (
                  <SelectItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="h-[340px] w-full">
            {yearsToPlot.length === 0 ? (
              <div className="h-full flex items-center justify-center text-sm text-muted-foreground">
                No monthly data to chart for this selection.
              </div>
            ) : (
              <ResponsiveContainer width="100%" height="100%">
                <LineChart
                  data={chartData}
                  margin={{ top: 8, right: 16, left: 0, bottom: 8 }}
                >
                  <CartesianGrid
                    strokeDasharray="3 3"
                    stroke="hsl(var(--border))"
                  />
                  <XAxis
                    dataKey="month"
                    stroke="hsl(var(--muted-foreground))"
                    tick={{ fontSize: 11 }}
                  />
                  <YAxis
                    stroke="hsl(var(--muted-foreground))"
                    tick={{ fontSize: 11 }}
                    tickFormatter={formatTick}
                  />
                  <Tooltip
                    contentStyle={{
                      backgroundColor: "hsl(var(--card))",
                      border: "1px solid hsl(var(--border))",
                      borderRadius: 6,
                      fontSize: 12,
                    }}
                    formatter={(value: any) => formatValue(Number(value))}
                  />
                  <Legend wrapperStyle={{ fontSize: 12 }} />
                  {yearsToPlot.map((year, idx) => (
                    <Line
                      key={year}
                      type="monotone"
                      dataKey={year}
                      name={year}
                      stroke={YEAR_COLOURS[idx % YEAR_COLOURS.length]}
                      strokeWidth={2}
                      dot={{ r: 3 }}
                    />
                  ))}
                </LineChart>
              </ResponsiveContainer>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default OverviewTab;
